import { FlexContent } from "@rothoblaas/design-system";
import type { CSSProperties, FC } from "react";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { ReadOnlyContext, useReadOnly } from "../../../components/ui/ReadOnlyGate";
import { resolveAccentColor } from "../../../constants/colors";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import type { Configuration } from "../../../http/deckDesigner/types/configurations";
import type { ConnectorMappingForSettings } from "../../../http/deckDesigner/types/connectorMappings";
import { GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING } from "../../../http/deckDesigner/types/groovingTypes";
import {
  buildDeckDesignerConfigurationDefaults,
  useAutoSaveDeckDesignerConfiguration,
  type DeckDesignerConfigurationFormValues,
} from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import useDeckSettingsApi from "../../../http/deckDesigner/useSettings";
import type { Calculation } from "../../../http/types/calculations";
import type { UnitSystem } from "../../../types";
import { getAsymmetricalGroovingMinimumBoardThickness } from "./boardThicknessConstants";
import { filterConnectorMappings } from "./connectorMappingFilter";
import styles from "./DeckDesigner.module.css";
import DeckDesignerForm from "./DeckDesignerForm";
import DeckDesignerResults from "./DeckDesignerResults";
import { DECK_DESIGNER_TAB_PARAMETER, type GoToTabs } from "./deckDesignerTabs";
import UnitSystemToggle from "./UnitSystemToggle";
import { useDerivedFormValue } from "../../../hooks/useDerivedFormValue";
import { useFieldsInError } from "./useFieldsInError";

export type DeckDesignerProps = {
  data?: Calculation<Configuration>;
  // The page header's buttons area (see Calculation): the unit system toggle is portaled there.
  headerButtonsContainer?: HTMLElement | null;
};

// Whether the selection still has a connector mapping it can be saved with.
const hasValidConnectorMapping = (
  values: DeckDesignerConfigurationFormValues,
  connectorMappings: ConnectorMappingForSettings[] | undefined,
): boolean => {
  const {
    substructureMaterial,
    fasteningType,
    groovingType,
    connectorFamily,
    secondaryItem,
    gap,
    connector,
    boardThickness,
    groovingHeight,
  } = values;
  const matchingConnectorMappings = connectorMappings
    ? filterConnectorMappings(connectorMappings, {
        substructureMaterial,
        fasteningType,
        groovingType,
        connectorFamily,
        secondaryItem,
      })
    : undefined;

  if (!matchingConnectorMappings?.length) {
    return false;
  }

  if (
    gap != null &&
    !matchingConnectorMappings.some(
      (connectorMapping) => connectorMapping.minimumGap <= gap && gap <= connectorMapping.maximumGap,
    )
  ) {
    return false;
  }

  const selectedConnectorFamily = connectorFamily ?? connector?.connectorFamily ?? null;

  if (
    selectedConnectorFamily?.maxBoardThickness != null &&
    boardThickness != null &&
    boardThickness > selectedConnectorFamily.maxBoardThickness
  ) {
    return false;
  }

  if (
    groovingHeight != null &&
    selectedConnectorFamily?.groovingMinHeight != null &&
    selectedConnectorFamily?.groovingMaxHeight != null &&
    (groovingHeight < selectedConnectorFamily.groovingMinHeight ||
      groovingHeight > selectedConnectorFamily.groovingMaxHeight)
  ) {
    return false;
  }

  if (
    groovingType?.code !== GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING ||
    groovingHeight == null ||
    boardThickness == null
  ) {
    return true;
  }

  return (
    boardThickness >=
    getAsymmetricalGroovingMinimumBoardThickness(groovingHeight, selectedConnectorFamily?.groovingThickness)
  );
};

// What resets the auto-select triangle history (see below) when it changes.
const getAutoSelectTriangleDependencyKey = (values: DeckDesignerConfigurationFormValues): string =>
  `${values.boardThickness}|${values.secondaryItem?.item.id}|${values.totalHeight}`;

// The form state, the settings, the autosave and the unit system. The form and the results are
// memoized parts with stable props: this re-renders on what it hands them (settings, errors, the
// calculation) and on navigation, not on every form change - what it derives from the values is
// subscribed (see useDerivedFormValue), and so is the autosave.
const DeckDesigner: FC<DeckDesignerProps> = (props: DeckDesignerProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const deckSettingsApi = useDeckSettingsApi();
  const settingsAborter = useRef(new AbortController());
  const isReadOnly = useReadOnly();
  const navigate = useNavigate();
  const location = useLocation();

  // Built once per loaded calculation, not on every props.data change: autosave replaces props.data on
  // each save (the optimistic echo, then the server's), and new defaults re-rendered every memoized
  // card twice per save. The cards compare against the loaded configuration, not the last save.
  const defaultValues = useMemo<DeckDesignerConfigurationFormValues>(
    () => buildDeckDesignerConfigurationDefaults(props.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.data?.id],
  );

  const formMethods = useForm<DeckDesignerConfigurationFormValues>({
    defaultValues,
    mode: "onChange",
  });
  const settingsSwr = deckSettingsApi.getDeckSettings(props.data?.id, settingsAborter.current.signal);
  // Without settings there is nothing valid to pick from: every field is disabled then, as for a viewer
  // (the failure itself is notified, see the settings load below). Memoized: the cards are, and a new
  // context value would re-render all of them.
  const isFormReadOnly = isReadOnly || !!settingsSwr.error;
  const readOnlyContextValue = useMemo(() => ({ isReadOnly: isFormReadOnly }), [isFormReadOnly]);

  // Only when a calculation is loaded, not on every props.data change: autosave mutates props.data on
  // each save (the optimistic echo, then the server's), and resetting to an older save while the user
  // keeps clicking would overwrite the auto-selected values (they aren't dirty), which the auto-select
  // triangle guard then refuses to pick again.
  const loadedCalculationIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!props.data || loadedCalculationIdRef.current === props.data.id) {
      return;
    }

    loadedCalculationIdRef.current = props.data.id;
    formMethods.reset(defaultValues, {
      keepDirtyValues: true,
      keepErrors: true,
    });
  }, [defaultValues, props.data, formMethods]);

  // Shared with ShapeAndSizeCard, which caches secondaryBase/secondaryHeight here across a
  // rectangular<->L-shape toggle (rectangular has no secondary fields, so they'd otherwise be
  // lost). Clearing the drawing must drop this cache too, or switching shape afterwards would
  // resurrect the pre-clear secondary dimensions.
  const secondaryDimensionsCacheRef = useRef<{
    secondaryBase: number | null;
    secondaryHeight: number | null;
  }>({ secondaryBase: defaultValues.secondaryBase, secondaryHeight: defaultValues.secondaryHeight });
  const resetSecondaryDimensionsCache = useCallback(() => {
    secondaryDimensionsCacheRef.current = { secondaryBase: null, secondaryHeight: null };
  }, []);

  const accentColor = resolveAccentColor(props.data?.module?.category?.color);

  // substructure, support and connector auto-select effects (spread across SubstructureCard and
  // ConnectorsCard) all filter each other's options via calculateValidSupportRange, so they can
  // otherwise auto-select each other in a cycle that never settles. Tracks (field, id, id, id)
  // states already visited during the current cascade and refuses to revisit one, regardless of
  // which card's effect is doing the selecting.
  const autoSelectTriangleHistoryRef = useRef<Set<string>>(new Set());
  const hasVisitedAutoSelectTriangleState = useCallback(
    (key: string) => autoSelectTriangleHistoryRef.current.has(key),
    [],
  );
  const rememberAutoSelectTriangleState = useCallback((key: string) => {
    autoSelectTriangleHistoryRef.current.add(key);
  }, []);
  const resetAutoSelectTriangleHistory = useCallback(() => {
    autoSelectTriangleHistoryRef.current = new Set();
  }, []);

  const autoSelectTriangleDependencyKey = useDerivedFormValue(formMethods, getAutoSelectTriangleDependencyKey);
  useEffect(() => {
    resetAutoSelectTriangleHistory();
  }, [autoSelectTriangleDependencyKey, resetAutoSelectTriangleHistory]);

  const connectorMappings = settingsSwr.data?.connectorMappings;
  const deriveHasValidConnectorMapping = useCallback(
    (values: DeckDesignerConfigurationFormValues) => hasValidConnectorMapping(values, connectorMappings),
    [connectorMappings],
  );
  const notifyAutoSaveError = useCallback(
    (err: unknown) => dispatch(showErrorNotification(i18n, err)),
    [dispatch, i18n],
  );

  // The mapping check runs inside the autosave, on the values it saves - not as a derived value, which
  // lags the form by one render and could let a just-invalidated selection through.
  useAutoSaveDeckDesignerConfiguration(props.data, formMethods, notifyAutoSaveError, deriveHasValidConnectorMapping);

  // Errors only as the set of fields in error (see useFieldsInError), not on every validation.
  const fieldsInError = useFieldsInError(formMethods);

  // Once the calculation is known, and again only if it changes: the settings are per calculation
  // (filtered by visibility plus what it uses), and autosave replacing props.data keeps the same id.
  const calculationId = props.data?.id;
  const { trigger: triggerSettings } = settingsSwr;
  // A failed load is notified too: the cards only show an inline "error loading data" on their fields.
  // i18n as a dependency also reloads them on a language change - they come localized.
  useEffect(() => {
    if (!calculationId) {
      return;
    }

    triggerSettings().catch((err) => dispatch(showErrorNotification(i18n, err)));
  }, [calculationId, dispatch, i18n, triggerSettings]);

  // Stable, reading the latest search from a ref: the memoized form and results take it as a prop.
  const locationSearchRef = useRef(location.search);
  useLayoutEffect(() => {
    locationSearchRef.current = location.search;
  });
  const goToTabs = useCallback<GoToTabs>(
    (tabIndexByUrlParameter) => {
      const params = new URLSearchParams(locationSearchRef.current);
      for (const [urlParameter, tabIndex] of Object.entries(tabIndexByUrlParameter)) {
        params.set(urlParameter, `${tabIndex}`);
      }
      navigate({ search: params.toString() });
    },
    [navigate],
  );

  // Switching unit system keeps the shape and its dimensions (saved in metric, so the new unit
  // just shows them, unrounded) and starts every other field over, live load included: the products
  // differ per market. Also clears the auto-select cache the cards keep alongside the form, and goes
  // back to the first tab since the later ones are locked again. Stable: the toggle is memoized.
  const resetConfigurationForUnitSystem = useCallback(
    (nextUnitSystem: UnitSystem) => {
      const {
        id,
        name,
        shape,
        base,
        height,
        secondaryBase,
        secondaryHeight,
        lShapeCutoutCorner,
        totalHeight,
        slopeDegrees,
        slopeArrow,
      } = formMethods.getValues();
      // keepDefaultValues: the emptied fields stay dirty against the loaded configuration, so the
      // props.data reset effect above (keepDirtyValues) can't bring the old values back before autosave
      // persists the empty ones.
      formMethods.reset(
        {
          ...buildDeckDesignerConfigurationDefaults(),
          id,
          name,
          unitSystem: nextUnitSystem,
          shape,
          base,
          height,
          secondaryBase,
          secondaryHeight,
          lShapeCutoutCorner,
          totalHeight,
          slopeDegrees,
          slopeArrow,
        },
        { keepDefaultValues: true },
      );
      resetAutoSelectTriangleHistory();
      goToTabs({ [DECK_DESIGNER_TAB_PARAMETER]: 0 });
    },
    [formMethods, goToTabs, resetAutoSelectTriangleHistory],
  );

  return (
    <ReadOnlyContext.Provider value={readOnlyContextValue}>
      <FormProvider {...formMethods}>
        {/* Portaled into the page header, but still rendered here so it stays inside FormProvider. */}
        {props.headerButtonsContainer &&
          createPortal(
            <UnitSystemToggle accentColor={accentColor} onChange={resetConfigurationForUnitSystem} />,
            props.headerButtonsContainer,
          )}
        <FlexContent padding noPaddingTop className={styles.pageFlex}>
          <div className={styles.workspace} style={{ "--accent-base": accentColor } as CSSProperties}>
            <DeckDesignerForm
              accentColor={accentColor}
              defaultValues={defaultValues}
              settings={settingsSwr.data}
              hasSettingsError={!!settingsSwr.error}
              isFormReadOnly={isFormReadOnly}
              cachedSecondaryDimensionsRef={secondaryDimensionsCacheRef}
              hasVisitedAutoSelectTriangleState={hasVisitedAutoSelectTriangleState}
              rememberAutoSelectTriangleState={rememberAutoSelectTriangleState}
              resetAutoSelectTriangleHistory={resetAutoSelectTriangleHistory}
              fieldsInError={fieldsInError}
              goToTabs={goToTabs}
            />
            <DeckDesignerResults
              calculationId={props.data?.id}
              configurationName={props.data?.name}
              projectName={props.data?.project?.name}
              accentColor={accentColor}
              hasFormErrors={fieldsInError.size > 0}
              onDimensionsCleared={resetSecondaryDimensionsCache}
              goToTabs={goToTabs}
            />
          </div>
        </FlexContent>
      </FormProvider>
    </ReadOnlyContext.Provider>
  );
};

export default DeckDesigner;
